/**
 * ConsentDocumentManager — author, version and publish consent copy (AD-01).
 *
 * Documents are scoped per service group because consent copy is
 * jurisdictional: an Irish pilot cannot reuse a UK document.
 *
 * Publishing is one-way. A published version is what somebody legally agreed
 * to, so it cannot be edited afterwards — a change is a new version, and
 * clients are re-asked. The UI states that before you publish rather than
 * refusing the edit afterwards.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, MenuItem, Stack, Chip,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip
} from '@mui/material';
import { Add as AddIcon, Publish as PublishIcon, Edit as EditIcon, Archive as ArchiveIcon } from '@mui/icons-material';
import api from '../../services/api';

const KEY_LABEL = {
  terms_of_service: 'Terms of service',
  privacy_policy: 'Privacy policy',
  hybrid_care: 'How this service works (hybrid care)'
};
const KEY_HELP = {
  terms_of_service: 'The service agreement.',
  privacy_policy: 'How personal and clinical data is handled.',
  hybrid_care: 'What the AI Supporter is, that a human therapist holds clinical responsibility, that transcripts are text-only and EU-hosted, and that this is not an emergency service. Also describe clinical-supervisor access.'
};
const STATUS_COLOR = { draft: 'default', published: 'success', retired: 'warning' };

export default function ConsentDocumentManager({ token }) {
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState(null);
  const [confirmPublish, setConfirmPublish] = useState(null);

  useEffect(() => {
    api.get('/service-groups', token)
      .then(res => {
        const payload = res.data;
        const list = Array.isArray(payload) ? payload
          : Array.isArray(payload?.data) ? payload.data
          : Array.isArray(payload?.serviceGroups) ? payload.serviceGroups : [];
        setGroups(list);
        if (list.length && !groupId) setGroupId(String(list[0]._id));
      })
      .catch(e => setError(e.response?.data?.msg || 'Could not load service groups'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const load = useCallback(async () => {
    if (!groupId) return;
    try {
      setLoading(true);
      const res = await api.get(`/admin/consent/documents?serviceGroupId=${groupId}`, token);
      setDocuments(res.data.documents || []);
    } catch (e) {
      setError(e.response?.data?.msg || 'Could not load consent documents');
    } finally {
      setLoading(false);
    }
  }, [groupId, token]);

  useEffect(() => { load(); }, [load]);

  const byKey = (key) => documents.filter(d => d.documentKey === key);
  const publishedFor = (key) => byKey(key).find(d => d.status === 'published');

  const save = async () => {
    try {
      setError('');
      if (editing._id) {
        await api.patch(`/admin/consent/documents/${editing._id}`, editing, token);
      } else {
        await api.post('/admin/consent/documents', { ...editing, serviceGroupId: groupId }, token);
      }
      setEditing(null);
      setNotice('Draft saved');
      await load();
    } catch (e) {
      setError(e.response?.data?.msg || 'Could not save');
    }
  };

  const publish = async () => {
    try {
      await api.post(`/admin/consent/documents/${confirmPublish._id}/publish`, {}, token);
      setConfirmPublish(null);
      setNotice('Published — clients will be asked to agree to this version');
      await load();
    } catch (e) {
      setError(e.response?.data?.msg || 'Could not publish');
    }
  };

  const retire = async (doc) => {
    try {
      await api.post(`/admin/consent/documents/${doc._id}/retire`, {}, token);
      await load();
    } catch (e) {
      setError(e.response?.data?.msg || 'Could not retire');
    }
  };

  const missing = Object.keys(KEY_LABEL).filter(k => !publishedFor(k));

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Consent documents</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Clients must agree to all three before the programme opens to them. Copy is per service group,
        because consent is jurisdictional.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

      <TextField
        select label="Service group" size="small" value={groupId}
        onChange={(e) => setGroupId(e.target.value)} sx={{ minWidth: 320, mb: 2 }}
      >
        {groups.map(g => <MenuItem key={g._id} value={String(g._id)}>{g.name}</MenuItem>)}
      </TextField>

      {!!groupId && missing.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {missing.length} of 3 documents have no published version
          ({missing.map(k => KEY_LABEL[k]).join(', ')}). Until all three are published, clients in this
          group cannot complete consent — the step tells them it is not ready rather than showing draft text.
        </Alert>
      )}

      {loading && <CircularProgress size={22} />}

      <Stack spacing={3}>
        {Object.keys(KEY_LABEL).map(key => (
          <Card key={key} variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
                <Box>
                  <Typography variant="h6">{KEY_LABEL[key]}</Typography>
                  <Typography variant="caption" color="text.secondary">{KEY_HELP[key]}</Typography>
                </Box>
                <Button
                  size="small" startIcon={<AddIcon />}
                  onClick={() => setEditing({ documentKey: key, version: '', title: KEY_LABEL[key], body: '' })}
                  disabled={!groupId}
                >
                  New version
                </Button>
              </Stack>

              {byKey(key).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No versions yet.
                </Typography>
              ) : (
                <Table size="small" sx={{ mt: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Version</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Published</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byKey(key).map(d => (
                      <TableRow key={d._id} hover>
                        <TableCell>{d.version}</TableCell>
                        <TableCell><Chip size="small" label={d.status} color={STATUS_COLOR[d.status]} /></TableCell>
                        <TableCell>{d.publishedAt ? new Date(d.publishedAt).toLocaleDateString() : '—'}</TableCell>
                        <TableCell align="right">
                          {d.status === 'draft' && (
                            <>
                              <Tooltip title="Edit draft">
                                <Button size="small" startIcon={<EditIcon />} onClick={() => setEditing(d)}>Edit</Button>
                              </Tooltip>
                              <Tooltip title="Publish — cannot be undone or edited afterwards">
                                <Button size="small" startIcon={<PublishIcon />} onClick={() => setConfirmPublish(d)}>Publish</Button>
                              </Tooltip>
                            </>
                          )}
                          {d.status === 'published' && (
                            <Tooltip title="Retire this version">
                              <Button size="small" color="warning" startIcon={<ArchiveIcon />} onClick={() => retire(d)}>Retire</Button>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="md" fullWidth>
        <DialogTitle>{editing?._id ? 'Edit draft' : 'New version'} — {KEY_LABEL[editing?.documentKey]}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Version" value={editing?.version || ''}
              onChange={(e) => setEditing(v => ({ ...v, version: e.target.value }))}
              helperText="Include the jurisdiction and date, e.g. 2026-08-IE-v1"
              fullWidth
            />
            <TextField
              label="Title shown to the client" value={editing?.title || ''}
              onChange={(e) => setEditing(v => ({ ...v, title: e.target.value }))} fullWidth
            />
            <TextField
              label="Body" value={editing?.body || ''}
              onChange={(e) => setEditing(v => ({ ...v, body: e.target.value }))}
              multiline minRows={14} fullWidth
              helperText={`${(editing?.body || '').length} characters. Plain text or markdown; shown read-only to the client.`}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!editing?.version || !editing?.title || !editing?.body}>
            Save draft
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmPublish} onClose={() => setConfirmPublish(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Publish this version?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Once published, this text cannot be edited. It becomes the wording clients legally agree to,
            and any change means a new version that every client is asked to accept again.
          </Alert>
          <Typography variant="body2">
            {KEY_LABEL[confirmPublish?.documentKey]} — version {confirmPublish?.version}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPublish(null)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={publish}>Publish</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
